import { Component, ElementRef, EventEmitter, Inject, LOCALE_ID, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';

import { CustomerARBOrderGridActionRenderComponent } from '../../../../orders/components/customerOrderGridActionrRenderer/customerARBOrderGridActionRenderer.component';
import {
  ApiService,
  AttachmentEntityType,
  CoreUiPartialState,
  getBrandDefaults,
  hasUnsavedChanges,
  isExternalUser,
  isProgrammeUser,
  MessageService,
  saveCustomerOrderDetailsForTask,
  SpinnerService,
} from '@pos-app/core-ui';
import { customersQuery } from '../../../+state/customers.selectors';
import { CustomersPartialState } from '../../../+state/customers.reducer';
import { FetchJdeOrder } from '../../../../orders/+state/orders.actions';
import { ORDER_ATTACHMENT_TYPE } from '@pos-app/data';
import { getUserAssignedCurrencyFormat } from '@pos-app/core-ui/lib/utils/locale-helper';
import { GetUserSalesPersonListAction } from '../../../+state/customers.actions';
import { ordersQuery } from '../../../../orders/+state/orders.selectors';
import { CoreUiModule } from '../../../../../../../../../libs/core-ui/src/lib/core-ui.module';
import { FormsModule } from '@angular/forms';
import { AgGridModule } from 'ag-grid-angular';
import { NgIf, AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-customers-arb-orders',
    templateUrl: './customers-arb-orders.component.html',
    styleUrls: ['./customers-arb-orders.component.scss'],
    imports: [CoreUiModule, FormsModule, AgGridModule, NgIf, AsyncPipe]
})
export class CustomersArbOrdersComponent implements OnInit, OnDestroy {
  @ViewChild('content') modalBox: ElementRef;
  @Output() onFetchOrderFromJDE = new EventEmitter();

  gridOptions: GridOptions;
  columnDefs: ColDef[];
  frameworkComponents;
  context;
  rowData = [];
  searchValue: string;
  orderDetails: any;
  isReadOnlyLineAttachment: boolean;
  ORDER_ATTACHMENT_TYPE = ORDER_ATTACHMENT_TYPE;

  private unSubscribe$ = new Subject<void>();
  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;

  public loading$ = new BehaviorSubject<boolean>(false);
  public isExternalUser$: Observable<boolean>;
  public isProgrammeUser$: Observable<boolean>;
  show = false;
  selectedSalesPerson: string;
  salesPeopleList: any;
  formValid: boolean = false;
  internalUser: boolean = false;
  allAttachments: any[];
  entityId: string;
  entityType: AttachmentEntityType;
  defaultCompanyCode: string = JSON.parse(localStorage.getItem('jdeSession')).CompanyCode;
  currencyCode: string;
  madeChanges: boolean = false;
  isShowOrderAttachment: boolean = false;
  isShowSalesPersonSelector: boolean = false;
  public loadingFetchingOrderFromJDE$: Observable<boolean>;

  constructor(
    private apiService: ApiService,
    private customerStore: Store<CustomersPartialState>,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private coreUiStore: Store<CoreUiPartialState>,
    private router: Router,
    private ngZone: NgZone,
    private messageService: MessageService,
    public spinnerService: SpinnerService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.frameworkComponents = {
      actionRenderer: CustomerARBOrderGridActionRenderComponent,
    };
    this.context = { componentParent: this };
    this.gridOptions = this.gridOptionsData();
  }

  ngOnInit(): void {
    this.initializeSubscriptions();
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  private initializeSubscriptions(): void {
    this.coreUiStore
      .select(getBrandDefaults)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        if (data) {
          this.currencyCode = data?.currrencyCode;
          this.fetchOpenArbOrders();
        }
      });

    this.isExternalUser$ = this.coreUiStore.select(isExternalUser);
    this.isProgrammeUser$ = this.coreUiStore.select(isProgrammeUser);

    this.isExternalUser$.pipe(takeUntil(this.unSubscribe$)).subscribe((isExternalUser) => {
      this.isProgrammeUser$.pipe(takeUntil(this.unSubscribe$)).subscribe((isProgrammeUser) => {
        this.internalUser = !(isExternalUser || isProgrammeUser);
        this.updateColumnDefs();
      });
    });

    this.loadingFetchingOrderFromJDE$ = this.customerStore.select(ordersQuery.getIsFetchingJdeOrder);
    this.customerStore.dispatch(new GetUserSalesPersonListAction());
  }

  fetchOpenArbOrders(): void {
    this.loading$.next(true);
    this.apiService
      .fetchOpenARBOrders()
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res) => {
          res?.orderList?.forEach((order) => {
            order.internalUser = this.internalUser;
          });
          this.rowData = res.orderList;
          this.loading$.next(false);
        },
        error: () => {
          this.loading$.next(false);
        },
        complete: () => {
          this.loading$.next(false);
        },
      });

    if (this.rowData) {
      this.gridApi?.setRowData(this.rowData);
    }
  }

  onGridReady(params): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  gridOptionsData(): GridOptions {
    return {
      defaultColDef: {
        resizable: true,
        sortable: true,
        width: 150,
        cellClass: 'cell-class ag-right-aligned-cell',
        headerClass: 'header-class ag-left-aligned-header',
      },
      columnDefs: this.getColumnDefs(),
    };
  }
  private updateColumnDefs(): void {
    const columnDefs = this.gridOptions.columnDefs || [];
    columnDefs.forEach((colDef: any) => {
      switch (colDef?.field) {
        case 'reference':
          colDef.headerName = this.internalUser ? 'REFERENCE' : 'YOUR REFERENCE';
          break;
        case 'orderNumber':
          colDef.headerName = this.internalUser ? 'ORDER' : 'ARB ORDER';
          break;
        case 'supplierOrderNumber':
          colDef.hide = this.internalUser;
          break;
        case 'supplierReference':
          colDef.hide = this.internalUser;
          break;
        case 'supplier':
          colDef.hide = this.internalUser;
          break;
        case 'workshopDate':
          colDef.hide = !this.internalUser;
      }
    });
    this.gridApi?.setColumnDefs(columnDefs);
  }

  private getColumnDefs(): ColDef[] {
    return [
      {
        headerName: 'ACTION',
        width: 150,
        cellRenderer: 'actionRenderer',
        cellStyle: { 'text-align': 'left' },
      },
      {
        headerName: 'YOUR REFERENCE',
        flex: 1,
        minWidth: 200,
        field: 'reference',
        cellStyle: { 'text-align': 'left' },
        onCellClicked: (params) => {
          this.getNewWidth(params);
        },
      },
      {
        headerName: 'ORDER STATUS',
        width: 150,
        field: 'orderStatus',
        cellStyle: { 'text-align': 'left' },
        onCellClicked: (params) => {
          this.getNewWidth(params);
        },
      },
      {
        headerName: 'ARB ORDER',
        width: 130,
        field: 'orderNumber',
        cellRenderer: (params) => this.createOrderLink(params),
        cellStyle: { 'text-align': 'left' },
      },
      {
        headerName: 'SUPPLIER ORDER',
        width: 170,
        field: 'supplierOrderNumber',
        cellStyle: { 'text-align': 'left' },
      },
      {
        headerName: 'CONTACT',
        width: 250,
        field: 'contact',
        cellStyle: { 'text-align': 'left' },
        onCellClicked: (params) => {
          this.getNewWidth(params);
        },
      },
      {
        headerName: 'PHONE',
        width: 130,
        field: 'phone',
        cellStyle: { 'text-align': 'left' },
      },
      {
        headerName: 'SUPPLIER REFERENCE',
        width: 200,
        field: 'supplierReference',
        cellStyle: { 'text-align': 'left' },
        onCellClicked: (params) => {
          this.getNewWidth(params);
        },
      },
      {
        headerName: 'REQUEST DATE',
        width: 160,
        field: 'requestedDate',
        comparator: this.dateComparator,
        cellStyle: { 'text-align': 'left' },
      },
      {
        headerName: 'ATP DATE',
        width: 150,
        field: 'ATPDateDesc',
        comparator: this.dateComparator,
        cellStyle: (params) => {
          const atpDate = params.data.ATPDate ? new Date(params.data.ATPDate) : null;
          const requestDate = params.data.requestedDate ? new Date(params.data.requestedDate) : null;
          if (atpDate && requestDate && atpDate < requestDate) {
            return { 'text-align': 'left', 'background-color': 'red', color: 'white' };
          }
          return { 'text-align': 'left' };
        },
        onCellClicked: (params) => {
          this.getNewWidth(params);
        },
      },
      {
        headerName: 'WORKSHOP DATE',
        width: 180,
        field: 'workshopDate',
        comparator: this.dateComparator,
        cellRenderer: (params) => {
          if (params.value) {
            const div = document.createElement('div');
            const link = document.createElement('a');
            link.href = 'javascript:void(0)';
            link.innerText = params.value;
            const workshopDate = params.data.workshopDate ? this.getValidDate(params.data.workshopDate) : null;
            const requestDate = params.data.requestedDate ? this.getValidDate(params.data.requestedDate) : null;
            if (workshopDate && requestDate && workshopDate < requestDate) {
              link.style.color = 'white';
              div.style.backgroundColor = 'red';
            }
            link.addEventListener('click', () => {
              this.router.navigateByUrl(`/pos/scheduler/${params.value}/timeline`);
            });
            div.appendChild(link);
            return div;
          }
          return params.value;
        },
        cellStyle: (params) => {
          const workshopDate = params.data.workshopDate ? this.getValidDate(params.data.workshopDate) : null;
          const requestDate = params.data.requestedDate ? this.getValidDate(params.data.requestedDate) : null;
          if (workshopDate && requestDate && workshopDate < requestDate) {
            return {
              'text-align': 'left',
              'background-color': 'red',
            };
          }
          return { 'text-align': 'left', color: 'black' };
        },
      },
      {
        headerName: 'TOTAL PRICE',
        width: 150,
        field: 'totalPrice',
        cellRenderer: (params) => this.displayPrice(params.value, params.data.currencyCode),
        cellStyle: { 'text-align': 'left' },
      },
    ];
  }
  getNewWidth(params): void {
    const textLength = (params.value?.length || 0) * 10;
    const minWidth = params.colDef.minWidth || params.colDef.width;
    if (textLength > minWidth) {
      return params.columnApi.setColumnWidth(params.column, params.value?.length * 12 + 50);
    } else {
      return params.columnApi.setColumnWidth(params.column, minWidth);
    }
  }

  private createOrderLink(params): HTMLAnchorElement {
    const link = document.createElement('a');
    link.href = 'javascript:void(0)';
    link.innerText = params.value;
    link.addEventListener('click', () => {
      this.fetchOrderFromJDE(params.data);
    });
    return link;
  }

  getValidDate(dateString: string): Date {
    const dateParts = dateString.split('/');
    if (dateParts.length === 3) {
      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // Month is 0-based in JavaScript
      const year = parseInt(dateParts[2], 10);
      return new Date(year, month, day);
    }
    return null;
  }

  dateFormatter(params): string {
    return moment(params.value).format('L <HH:mm>');
  }

  dateComparator(date1: string, date2: string): number {
    const dateParts1 = date1.split('/').map((part) => parseInt(part, 10));
    const dateParts2 = date2.split('/').map((part) => parseInt(part, 10));
    const d1 = new Date(dateParts1[2], dateParts1[1] - 1, dateParts1[0]);
    const d2 = new Date(dateParts2[2], dateParts2[1] - 1, dateParts2[0]);
    return d1.getTime() - d2.getTime();
  }

  attachedItem(cell, isReadOnly): void {
    this.entityId = `${cell.orderNumber.toString()}|${cell.orderType}|${this.defaultCompanyCode}`;
    this.entityType = 'order';
    this.orderDetails = cell;
    this.isReadOnlyLineAttachment = isReadOnly;
    this.isShowOrderAttachment = true;
    this.loading$.next(true);
    this.apiService
      .loadAttachmentForOrder({
        orderNumber: cell.orderNumber,
        orderType: cell.orderType,
        JDEOrderType: cell?.JDEOrderType,
        JDECompany: cell?.JDECompany,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res: any) => {
        this.loading$.next(false);
        this.allAttachments = res?.attachmentIDArray;
        this.openModalBox();
      });
  }

  loadAllAttachments(attachments: any): void {
    this.madeChanges = true;
    this.allAttachments = attachments;
  }

  downloadFile(item): void {
    this.apiService.downloadFile(item._id, item.name).subscribe();
  }

  closeModal(): void {
    this.modalService.dismissAll();
  }

  handleCloseAttachmentModel(event): void {
    if (this.isShowOrderAttachment) {
      if (this.madeChanges) {
        this.saveAttachments(event);
      } else {
        this.unloadAttachments(event);
      }
    }
  }

  private saveAttachments(event): void {
    this.loading$.next(true);
    this.modalService.dismissAll();
    this.apiService
      .saveAttachmentForOrder({
        orderNumber: this.orderDetails.orderNumber,
        orderType: this.orderDetails.orderType,
        JDEOrderType: this.orderDetails?.JDEOrderType,
        JDECompany: this.orderDetails?.JDECompany,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res: any) => {
        this.loading$.next(false);
        this.allAttachments = res?.attachmentIDArray;
        this.madeChanges = false;
        if (event) {
          this.isShowOrderAttachment = false;
          this.fetchOpenArbOrders();
        }
      });
  }

  private unloadAttachments(event): void {
    this.loading$.next(true);
    this.modalService.dismissAll();
    this.apiService
      .unloadAttachmentForOrder({
        orderNumber: this.orderDetails.orderNumber,
        orderType: this.orderDetails.orderType,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res: any) => {
        this.loading$.next(false);
        this.allAttachments = res?.attachmentIDArray;
        this.isShowOrderAttachment = false;
        if (event) {
          this.isShowOrderAttachment = false;
        }
      });
  }

  formatTime(time: string): string {
    // JDE time doesn't put a 0 at the start when less than 10am
    if (parseInt(time.slice(0, 2)) > 24) {
      return ['0' + time.slice(0, 1), ':', time.slice(1, 3)].join('');
    } else {
      return [time.slice(0, 2), ':', time.slice(2, 4)].join('');
    }
  }

  fetchOrderFromJDE(data): void {
    this.customerStore.dispatch(
      new FetchJdeOrder({
        orderNumber: data.orderNumber,
        orderType: data.orderType,
        JDEOrderType: data?.JDEOrderType,
        JDECompany: data?.JDECompany,
      })
    );
  }

  updateRequestDate(newDate: string, data: any): void {
    if (newDate === data.requestedDate) {
      return;
    }
    const [day, month, year] = newDate.split('/').map(Number);
    const dateObject = new Date(year, month - 1, day);

    // Get today's date at midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateObject >= today) {
      const dataHeader = {
        requestDate: newDate,
        orderNumber: data.orderNumber,
        JDEOrderType: data?.JDEOrderType,
        JDECompany: data?.JDECompany,
      };

      this.apiService
        .updateRequestDateOfSavedOrder(dataHeader)
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe({
          next: (res) => {
            if (res) {
              this.toastr.success('Request date updated successfully');
              this.fetchOpenArbOrders();
            }
          },
          error: (error) => {
            this.toastr.error(error?.ErrorMessage || 'An error occurred while updating the request date');
          },
        });
    } else {
      this.toastr.error("Request date cannot be earlier than today's date");
    }
  }

  displayPrice(price: number, currencyCode: string = ''): string {
    if (!price) {
      return '';
    }
    if (currencyCode !== '') {
      this.currencyCode = currencyCode;
    }
    return getUserAssignedCurrencyFormat(price, this.locale, this.currencyCode);
  }

  changeSalesPerson(cell): void {
    this.orderDetails = cell;

    this.customerStore
      .select(customersQuery.getUserSalespersonList)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        if (data) {
          this.salesPeopleList = data;
          if (this.salesPeopleList?.length > 0) {
            this.isShowSalesPersonSelector = true;
            this.openModalBox();
          } else {
            this.toastr.error('No salesperson list found');
          }
        }
      });
  }

  reloadData(): void {
    this.fetchOpenArbOrders();
    this.selectedSalesPerson = null;
    this.modalService.dismissAll();
  }

  loadTask(data): void {
    this.coreUiStore.dispatch(saveCustomerOrderDetailsForTask({ customerOrderDetails: data }));
    this.ngZone.run(() => {
      this.router.navigate([`central/task-search-order/${data.orderNumber}/${data.orderType}`]);
    });
  }

  addNewTaskForOrder(data): void {
    this.orderDetails = data;
    this.coreUiStore.dispatch(saveCustomerOrderDetailsForTask({ customerOrderDetails: { ...data, newTask: true } }));
    this.router.navigate([`central/task/new`]);
  }

  openModalBox(): void {
    this.modalService
      .open(this.modalBox, {
        windowClass: 'garage-modal',
        size: 'xl',
        backdrop: 'static',
      })
      .result.then(
        () => {
          this.resetModalFlags();
        },
        () => {
          this.resetModalFlags();
        }
      );
  }

  private resetModalFlags(): void {
    this.isShowOrderAttachment = false;
    this.isShowSalesPersonSelector = false;
  }

  saveTask(objectToUpdate): void {
    this.apiService.maintainTask(objectToUpdate).subscribe((res) => {
      if (res?.ErrorFlag === '0') {
        this.toastr.success('Task Updated Successfully');
        this.formValid = false;
        this.coreUiStore.dispatch(hasUnsavedChanges({ unsavedChanges: false }));
        this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: false }));
        this.modalService.dismissAll();
        this.fetchOpenArbOrders();
      }
    });
  }

  exportGrid() {
    if (this.gridApi) {
      this.gridApi.exportDataAsCsv({
        fileName: `ARBOrders_${moment().format('YYYYMMDD_HHmmss')}.csv`,
        columnKeys: [
          'reference',
          'orderStatus',
          'orderNumber',
          'supplierOrderNumber',
          'contact',
          'phone',
          'ordsupplierReferenceerDate',
          'requestedDate',
          'ATPDateDesc',
          'workshopDate',
          'totalPrice',
        ],
        processCellCallback: (params) => {
          if (params.column.getColId() === 'totalPrice') {
            if (params.value === null || params.value === undefined || params.value === '') {
              return '';
            }
            return getUserAssignedCurrencyFormat(params.value, this.locale, this.currencyCode);
          }
          return params.value;
        },
      });
    }
  }
}

